import PropTypes from 'prop-types';

import { IconButton } from '@mui/material';
import { MIcon, MAdaptativeMenu } from '../../../@material-extend';
import useMAdaptativeMenu from '../../../@material-extend/MAdaptativeMenu/useMAdaptativeMenu';
import { getApprovalStatus } from '../DocumentsView/helpers/documentsHelpers';

/**
 * Renders a menu-triggering button for document actions, adapting its options based on the document's status.
 * This component provides a user interface for executing actions like approval or review directly from a dynamic menu.
 * It triggers a modal for confirmations and can update the selected file state.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} [props.file={}] - The file object associated with the document, used to determine the document's approval status.
 * @param {string} [props.name=''] - A name to display as the title of the action menu.
 * @param {function} props.handleOpenModalConfirmCancellation - Function to open a confirmation modal for critical actions.
 * @param {function} props.setSelectedFile - Function to set the currently selected file in the state.
 * @param {boolean} props.open - Boolean state indicating whether the adaptive menu is open or closed.
 * @param {function} props.handleOpen - Function to handle the opening of the adaptive menu.
 * @param {any} props.anchorEl - The DOM element used as the anchor for positioning the menu.
 * @param {function} props.handleClose - Function to handle the closing of the adaptive menu.
 * @returns {JSX.Element|null} A button that triggers an adaptive menu for document actions, or null if there are no actions available or the document does not meet the approval status.
 */
const DocumentAction = (props) => {
  const { file = {}, name = '', handleOpenModalConfirmCancellation, setSelectedFile } = props;
  const approvalStatus = getApprovalStatus(file);
  const { handleClose, anchorEl, handleOpen, open } = useMAdaptativeMenu();

  const options = [
    {
      caption: "Cette demande d'ajout de document est en attente d'approbation par un manager",
      filled: true,
      filename: file.filename,
      icon: 'delete',
      iconVariant: 'critical',
      id: 'delete_demand',
      text: 'Annuler la demande',
      handler: (e) => handleDeleteDemand(e, file)
    }
  ];

  const handleDeleteDemand = (e, file) => {
    setSelectedFile(file);
    handleClose();
    handleOpenModalConfirmCancellation(e, file);
  };

  if (!options.length || !approvalStatus) {
    return;
  }

  return (
    <>
      <IconButton
        aria-controls={open ? 'document-action-button' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="document-action-button"
        onClick={handleOpen}
      >
        <MIcon icon="more_horiz" variant="neutral-faded" />
      </IconButton>

      {open && (
        <MAdaptativeMenu
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          handleClose={handleClose}
          open={open}
          options={options}
          title={name}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        />
      )}
    </>
  );
};

DocumentAction.propTypes = {
  file: PropTypes.object,
  name: PropTypes.string,
  setSelectedFile: PropTypes.func.isRequired,
  handleOpenModalConfirmCancellation: PropTypes.func.isRequired
};

export default DocumentAction;
